import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { SocketEventHandler, SocketManagerContext } from './SocketManagerContext';
import { IEntity } from '../../types/api/entities/IEntity';
import { windowLogger } from '../Logger/windowLogger';

type SocketSubscription<T extends IEntity = any> = {
  event: string;
  callback: SocketEventHandler<T>;
};

type SocketManagerProps = {
  endpoint: string;
  loggedIn: boolean;
};

export function SocketManager({ endpoint, loggedIn, children }: PropsWithChildren<SocketManagerProps>) {
  const [socketFactory, setSocketFactory] = useState<typeof io>();
  useEffect(() => {
    if (loggedIn) {
      import(/* webpackChunkName: "socketio" */ 'socket.io-client').then(({ default: module }) =>
        setSocketFactory((_: undefined | typeof io) => module),
      );
    }
  }, [loggedIn]);
  const subsRef = useRef<SocketSubscription[]>([]);

  const dispatchEvent = (eventType: string, message: unknown) => {
    const subs = subsRef.current;
    subs.filter(sub => sub.event === eventType).forEach(sub => sub.callback(message, eventType));
  };
  useEffect(() => {
    if (socketFactory) {
      const newSocket = socketFactory(endpoint, {
        forceNew: false,
        reconnectionAttempts: 5,
        reconnectionDelay: 3000,
        rememberUpgrade: true,
      });
      newSocket.on('connect', () => {
        windowLogger.info('SocketsManager connected!');
      });

      // handle 'disconnect'
      newSocket.on('disconnect', (reason: string) => {
        // see https://socket.io/docs/v4/client-socket-instance/#disconnect
        // intentional server-side close after logout: reason = "io server disconnect"
        //  => socket.io will NOT automatically try to reconnect. When the user logs in later,
        //     a browser navigation is triggered and all scripts including the SocketManagers are newly initialized.
        // unintended connection loss (e.g. server crash) => reason = "transport close"
        //  => socket.io will automatically try to reconnect
        // timout during ping: reason = "ping timout"
        // => socket.io will automatically try to reconnect
        windowLogger.info(`SocketsManager disconnected (reason: ${reason}).`);
      });
      newSocket.onAny(dispatchEvent);
      return () => {
        newSocket.disconnect();
        windowLogger.info('deconstruct');
      };
    }
  }, [socketFactory, endpoint]);

  const subscribe = useCallback((event: string, callback: SocketEventHandler<any>) => {
    const subscription = { event, callback };
    subsRef.current = [...subsRef.current, subscription];
    return () => {
      subsRef.current = subsRef.current.filter(sub => sub !== subscription);
    };
  }, []);

  return <SocketManagerContext.Provider value={subscribe}>{children}</SocketManagerContext.Provider>;
}
